import React from 'react'

// Step type definitions
export type StepType = 'userText' | 'systemText' | 'interaction' | 'code' | 'restart'

export interface BaseStep {
  type: StepType
  id?: string
  delay?: number
  showProcessing?: {
    mode: 'dots' | 'question'
    duration: number
  }
}

export interface TextStep extends BaseStep {
  type: 'userText' | 'systemText'
  content: string | React.ReactNode | React.ReactNode[]
  classNames?: string
}

export interface InteractionStep extends BaseStep {
  type: 'interaction'
  content: string
  buttonText?: string
  onButtonClick?: () => void
  showProcessingBeforeButton?: boolean
  processingMode?: 'dots' | 'question'
}

export interface CodeStep extends BaseStep {
  type: 'code'
  content: React.ReactNode[]
  highlightedLines?: number[]
  highlightColor?: 'red' | 'blue' | 'green'
}

export interface RestartStep extends BaseStep {
  type: 'restart'
}

export type Step = TextStep | InteractionStep | CodeStep | RestartStep

// Helper function to extract text from React nodes
export const extractTextFromReactNode = (node: React.ReactNode): string => {
  if (typeof node === 'string') {
    return node
  }
  if (typeof node === 'number') {
    return node.toString()
  }
  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode }
    if (props.children) {
      if (Array.isArray(props.children)) {
        return props.children.map(extractTextFromReactNode).join('')
      } else {
        return extractTextFromReactNode(props.children)
      }
    }
  }
  return ''
}

// Generic function to initialize steps with dynamic IDs and callbacks
export const initializeSteps = (
  steps: Step[],
  continueFromInteraction: (stepIndex: number) => void,
  idPrefix: string = 'step'
) => {
  // Dynamically assign IDs and update interaction callbacks
  steps.forEach((step, index) => {
    step.id = `${idPrefix}-${index}`

    // Update interaction callbacks to automatically continue from next step
    if (step.type === 'interaction') {
      const interactionStep = step as InteractionStep
      const nextStepIndex = index + 1
      interactionStep.onButtonClick = () => continueFromInteraction(nextStepIndex)
    }
  })
}
